/* =============================================================================
   SUPPLY: product.js: variant picker, gallery, quick view, recommendations, sticky ATC
   ============================================================================= */
(function () {
  'use strict';
  const theme = window.theme || {};
  const formatMoney = theme.formatMoney || ((c) => `$${(c / 100).toFixed(2)}`);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.sectionId = this.dataset.section;
      // Start from the parent: the picker carries data-section itself, and closest() would return the picker.
      this.root = this.parentElement?.closest('[data-section]') || this.closest('.quick-view') || document;
      this.addEventListener('change', (e) => { if (e.target.matches('[data-index]')) this.onChange(); });
      this.markAvailability();
    }
    get selectedOptions() {
      const opts = [];
      this.querySelectorAll('.variant-picker__group').forEach((group, i) => {
        const select = group.querySelector('select');
        if (select) opts[i] = select.value;
        else { const checked = group.querySelector('input:checked'); opts[i] = checked ? checked.value : null; }
      });
      return opts;
    }
    findVariant(options) {
      return this.variants.find((v) => v.options.every((o, i) => o === options[i]));
    }
    onChange() {
      const options = this.selectedOptions;
      let variant = this.findVariant(options);
      // If exact combination doesn't exist, keep the changed option and find the first variant that has it
      if (!variant) {
        const changedGroup = document.activeElement?.closest('.variant-picker__group');
        const idx = changedGroup ? parseInt(changedGroup.dataset.optionIndex, 10) : 0;
        variant = this.variants.find((v) => v.options[idx] === options[idx]) || null;
        if (variant) this.setSelection(variant.options);
      }
      this.querySelectorAll('.variant-picker__group').forEach((group, i) => {
        const v = group.querySelector('[data-selected-value]');
        // Show the tidied label (data-label / option text), not the raw catalogue value.
        const label = group.querySelector('input:checked')?.dataset.label || group.querySelector('select')?.selectedOptions[0]?.textContent;
        if (v) v.textContent = label || this.selectedOptions[i] || '';
      });
      this.markAvailability();
      this.updateProduct(variant);
    }
    setSelection(options) {
      this.querySelectorAll('.variant-picker__group').forEach((group, i) => {
        const select = group.querySelector('select');
        if (select) select.value = options[i];
        else group.querySelectorAll('input').forEach((input) => { input.checked = input.value === options[i]; });
      });
    }
    markAvailability() {
      const options = this.selectedOptions;
      this.querySelectorAll('.variant-picker__group').forEach((group, i) => {
        group.querySelectorAll('input').forEach((input) => {
          const test = options.slice(); test[i] = input.value;
          const v = this.findVariant(test);
          const pill = group.querySelector(`label[for="${input.id}"]`);
          if (pill) pill.classList.toggle('is-unavailable', !v || !v.available);
        });
      });
    }
    updateProduct(variant) {
      const root = this.root;
      const idInput = root.querySelector('[data-variant-id]');
      const addBtn = root.querySelector('[data-add-to-cart]');
      const addLabel = root.querySelector('[data-add-label]');
      const availability = root.querySelector('[data-availability]');
      const sku = root.querySelector('[data-sku]');
      const stickyPrice = root.querySelector('[data-sticky-price]');
      const priceWrap = root.querySelector('[data-price-wrapper] [data-price]');
      const dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
      if (dynamicCheckout) dynamicCheckout.hidden = !variant || !variant.available;
      if (!variant) {
        if (addBtn) { addBtn.disabled = true; }
        if (addLabel) addLabel.textContent = theme.strings?.unavailable || 'Unavailable';
        if (availability) { availability.textContent = theme.strings?.unavailable || 'Unavailable'; availability.classList.add('product-info__stock--out'); }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (addBtn) addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? (theme.strings?.addToCart || 'Add to cart') : (theme.strings?.soldOut || 'Sold out');
      if (availability) { availability.textContent = variant.available ? (availability.dataset.inStock || 'In stock') : (theme.strings?.soldOut || 'Sold out'); availability.classList.toggle('product-info__stock--out', !variant.available); }
      if (sku) sku.textContent = variant.sku ? `SKU ${variant.sku}` : '';
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (priceWrap) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceWrap.classList.toggle('price--on-sale', !!onSale);
        let html = '';
        if (onSale) {
          const saved = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          html = `<span class="price__sale" data-price-sale>${formatMoney(variant.price)}</span><s class="price__compare"><span class="visually-hidden">Regular price</span>${formatMoney(variant.compare_at_price)}</s><span class="price__save">Save ${saved}%</span>`;
        } else {
          html = `<span class="price__regular" data-price-regular>${formatMoney(variant.price)}</span>`;
        }
        priceWrap.innerHTML = html;
      }
      // Gallery
      if (variant.featured_media) {
        const gallery = root.querySelector('product-gallery');
        if (gallery) gallery.goToMedia(variant.featured_media.id);
      }
      // URL (main product page only)
      if (root === document || root.closest?.('.product-page') || root.classList?.contains('product-page')) {
        if (!root.closest || !root.closest('.quick-view')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(this.querySelectorAll('.gallery__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.gallery__thumb'));
      this.current = this.querySelector('[data-gallery-current]');
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(parseInt(t.dataset.index, 10))));
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.index > 0) this.go(this.index, true);
      this.track?.addEventListener('scroll', theme.debounce ? theme.debounce(() => this.syncFromScroll(), 60) : () => this.syncFromScroll(), { passive: true });
    }
    go(i, instant) {
      if (!this.slides.length) return;
      i = (i + this.slides.length) % this.slides.length;
      this.index = i;
      const slide = this.slides[i];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId == id);
      if (i >= 0) this.go(i);
    }
    syncFromScroll() {
      const x = this.track.scrollLeft; const w = this.track.clientWidth;
      const i = Math.round(x / Math.max(w, 1));
      if (i !== this.index) { this.index = i; this.setActive(i); }
    }
    setActive(i) {
      this.slides.forEach((s, j) => s.classList.toggle('is-active', j === i));
      this.thumbs.forEach((t, j) => { t.classList.toggle('is-active', j === i); if (j === i) { t.setAttribute('aria-current', 'true'); t.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } else t.removeAttribute('aria-current'); });
      if (this.current) this.current.textContent = i + 1;
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ---------- Quick view ---------- */
  function ensureQuickViewModal() {
    let dialog = document.getElementById('QuickView');
    if (dialog) return dialog;
    const root = document.getElementById('QuickViewRoot') || document.body;
    dialog = document.createElement('dialog');
    dialog.id = 'QuickView';
    dialog.className = 'modal quick-view-modal';
    dialog.setAttribute('aria-label', 'Quick view');
    dialog.innerHTML = `<div class="modal__backdrop" data-close-drawer></div><div class="modal__panel"><button class="icon-btn modal__close" type="button" aria-label="Close" data-close-drawer><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg></button><div class="modal__body" data-quick-view-body><div class="modal__loading"><span class="spinner"></span></div></div></div>`;
    root.appendChild(dialog);
    return dialog;
  }
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    const url = btn.getAttribute('data-quick-view');
    const dialog = ensureQuickViewModal();
    const body = dialog.querySelector('[data-quick-view-body]');
    body.innerHTML = '<div class="modal__loading"><span class="spinner"></span></div>';
    theme.Drawer.open(dialog, btn);
    try {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('.quick-view');
      body.innerHTML = '';
      if (content) body.appendChild(content); else body.innerHTML = '<p style="padding:2rem">Unable to load product.</p>';
      theme.observeReveals && theme.observeReveals(body);
    } catch (err) {
      body.innerHTML = '<p style="padding:2rem">Unable to load product.</p>';
    }
  });

  /* ---------- Recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) { this.innerHTML = fresh.innerHTML; theme.observeReveals && theme.observeReveals(this); }
          else this.remove();
        } catch (e) { /* noop */ }
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries.some((en) => en.isIntersecting)) { io.disconnect(); load(); } }, { rootMargin: '400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Sticky add-to-cart (mobile) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.target);
      const addBtn = target?.querySelector('[data-add-to-cart]');
      if (!target || !addBtn) return;
      this.hidden = false;
      this.querySelector('[data-sticky-add]')?.addEventListener('click', () => { addBtn.click(); });
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => this.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0));
      }, { threshold: 0 });
      io.observe(addBtn);
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  /* ---------- Details nav: open the target accordion & highlight ---------- */
  document.querySelectorAll('[data-details-nav] a').forEach((a) => {
    a.addEventListener('click', (e) => {
      const target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.open = true;
      const y = target.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 72) - 16;
      window.scrollTo({ top: y, behavior: 'smooth' });
      document.querySelectorAll('[data-details-nav] a').forEach((x) => x.classList.toggle('is-active', x === a));
    });
  });
})();
